import { overLimit, PAGE_LIMIT, remapAssets, type NewObject, type SceneStore } from '@os/doc';
import type { Point } from '@os/engine';
import { pasteOffset } from '../clipboard/clipboard';
import { readBoardArchive, type BoardArchive } from './archive';

const ext = (mime: string) => (mime === 'image/gif' ? 'gif' : 'webp');

const defaultFetch = async (url: string): Promise<Blob | null> => {
  try {
    const res = await fetch(url, { mode: 'cors' });
    return res.ok ? await res.blob() : null;
  } catch {
    return null;
  }
};

/** A file whose pages would take the board past PAGE_LIMIT (P14 Ruling 9). */
const tooManyPages = () => Object.assign(new Error('too_many_pages'), { code: 'too_many_pages' as const });
const boardFull = () => Object.assign(new Error('board_full'), { code: 'board_full' as const });

/**
 * "Import a file here" (plan P9 Ruling 2): the file's objects added to the open board like a paste centred on `at`,
 * with new ids (parents and connector ends follow), as one undoable step. `ingest` stores an image as an asset of
 * this board (deduplicated by `sha256` when known) and gives its id; an image without content keeps its old id.
 * A one-page file lands on the active page; each page of a longer file becomes a new last page (P14 Ruling 9).
 */
export async function importIntoBoard(o: {
  store: SceneStore;
  /** A `.osboard` or JSON file, or a file already read (a Figma import, P11b). */
  input: File | BoardArchive;
  at: Point;
  ingest(blob: Blob, name: string, sha256: string | null): Promise<string | null>;
  fetchBlob?(url: string): Promise<Blob | null>;
  /** Stops the import before anything is inserted. */
  signal?: AbortSignal;
}): Promise<{ ids: string[]; skipped: number; missing: number }> {
  if (o.store.readOnly) throw new Error('read_only');
  const archive = o.input instanceof File ? await readBoardArchive(o.input) : o.input;
  const paged = archive.file.pages.length > 1;
  if (paged && o.store.pages().length + archive.file.pages.length > PAGE_LIMIT) throw tooManyPages();
  // Before any image is ingested: the board limits refuse the whole batch anyway (P12a).
  const adds = { objects: archive.file.objects.length, images: archive.file.objects.filter((obj) => obj.type === 'image').length };
  if (overLimit(o.store.counts(), adds)) throw boardFull();
  const map = new Map<string, string>();
  // Images whose asset the file does not list at all, then those whose content cannot be read.
  let missing = archive.missing.filter((id) => !archive.file.assets.some((a) => a.id === id)).length;
  for (const a of archive.file.assets) {
    const blob = archive.blobs.get(a.id) ?? (a.url ? await (o.fetchBlob ?? defaultFetch)(a.url) : null);
    const id = blob ? await o.ingest(blob, `${a.id}.${ext(a.mime)}`, a.sha256) : null;
    if (id) map.set(a.id, id);
    else missing++;
    o.signal?.throwIfAborted();
  }
  o.signal?.throwIfAborted();
  if (o.store.readOnly) throw new Error('read_only');
  const source = remapAssets(archive.file.objects, map);
  const item = (obj: (typeof source)[number]) => {
    const { id, z: _z, pageId: _p, ...data } = obj;
    void _z;
    void _p;
    return { ref: id, data: data as NewObject };
  };
  const objects = source.map(item);
  const offset = pasteOffset({ v: 1, objects }, o.at);
  if (!paged) return { ids: o.store.commands.insertObjects(objects, { offset }), skipped: archive.skipped, missing };
  const pages = archive.file.pages.map((p) => ({ ref: p.id, name: p.name, items: source.filter((obj) => obj.pageId === p.id).map(item) }));
  const out = o.store.commands.insertPages(pages, { offset });
  if (!out) throw tooManyPages();
  return { ids: out.ids, skipped: archive.skipped, missing };
}
